import gsap from 'gsap';

const CRT_LOW_OPACITY = 0.5;
const CRT_HIGH_OPACITY = 1;
const CRT_OFF_OPACITY = 0;
const CRT_REEL_PLAY_OPACITY = 0.1;
const CRT_WARMUP_FRAMES = 72;
const SECTION_CANVAS_FADE_DURATION = 2;

export class Back {
  setUp(e) {
    this.e = e;
    this.enabled = !e.mobile;
    this.currentSection = 'reel';
    this.reelCanvasHidden = false;
    this.crt = null;
    this.renderer = null;
    this.scene3D = null;
    this.camera = null;
    this.crtInitPromise = null;
    this.introWarmupRequested = false;
    this.introWarmupPending = false;
    this.introWarmupFrames = 0;
    this.introOpacityPlayed = false;

    if (!this.enabled) {
      this.disableCrt();
      return;
    }

    this.enableCrtUi();
    this.crtInitPromise = this.initCrt();
  }

  disableCrt() {
    document.body.classList.remove('crt-bg-active', 'crt-intro-active', 'crt-print-page');
    document.body.style.background = '#000000';

    const canvasHolder = document.getElementById('canvasHolder');
    const scanlines = document.querySelector('.crt-scanlines');
    const vignette = document.querySelector('.crt-vignette');

    if (canvasHolder) {
      canvasHolder.style.display = 'none';
      canvasHolder.replaceChildren();
    }
    if (scanlines) scanlines.style.display = 'none';
    if (vignette) vignette.style.display = 'none';

    const pageGradient = document.getElementById('pageGradient');
    if (pageGradient) {
      gsap.killTweensOf(pageGradient);
      pageGradient.style.opacity = '0';
    }
  }

  enableCrtUi() {
    const canvasHolder = document.getElementById('canvasHolder');
    const scanlines = document.querySelector('.crt-scanlines');
    const vignette = document.querySelector('.crt-vignette');

    document.body.classList.add('crt-bg-active', 'crt-intro-active');
    document.body.style.background = '#000000';
    if (canvasHolder) {
      canvasHolder.style.display = '';
      canvasHolder.replaceChildren();
    }
    if (scanlines) scanlines.style.display = 'block';
    if (vignette) vignette.style.display = 'block';

    const pageGradient = document.getElementById('pageGradient');
    if (pageGradient) {
      gsap.killTweensOf(pageGradient);
      gsap.set(pageGradient, { opacity: 0 });
    }
  }

  initCrt() {
    const canvasHolder = document.getElementById('canvasHolder');
    if (!canvasHolder) {
      return Promise.resolve(null);
    }

    return import('./crtBackground.js')
      .then(({ CrtBackground }) => {
        if (!this.enabled) {
          return null;
        }

        this.crt = new CrtBackground(canvasHolder);
        this.scene3D = this.crt.scene;
        this.camera = this.crt.camera;
        this.renderer = this.crt.renderer;

        const canvas = this.getCanvasEl();
        if (canvas) {
          this.killCanvasOpacityTween();
          gsap.set(canvas, { opacity: CRT_OFF_OPACITY });
        }

        this.startIntroWarmupWhenReady();
        return this.crt;
      });
  }

  buildScene() {
    if (!this.enabled || this.introOpacityPlayed) return;
    this.introWarmupRequested = true;
    this.startIntroWarmupWhenReady();
  }

  startIntroWarmupWhenReady() {
    if (!this.enabled || !this.introWarmupRequested || this.introOpacityPlayed) {
      return;
    }

    const beginWarmup = () => {
      if (!this.crt || this.introWarmupPending || this.introOpacityPlayed) {
        return;
      }
      this.introWarmupPending = true;
      this.introWarmupFrames = 0;
    };

    if (this.crt) {
      beginWarmup();
      return;
    }

    if (this.crtInitPromise) {
      this.crtInitPromise.then(beginWarmup);
    }
  }

  update() {
    if (!this.enabled || !this.crt) return;
    this.crt.update();

    if (!this.introWarmupPending || this.introOpacityPlayed) return;

    this.introWarmupFrames += 1;
    if (this.introWarmupFrames < CRT_WARMUP_FRAMES) return;

    this.introWarmupPending = false;
    this.introOpacityPlayed = true;
    this.playIntroOpacitySequence();
  }

  getCanvasEl() {
    return this.renderer?.domElement ?? null;
  }

  killCanvasOpacityTween() {
    const canvas = this.getCanvasEl();
    if (canvas) gsap.killTweensOf(canvas);
  }

  playIntroOpacitySequence() {
    const canvas = this.getCanvasEl();
    if (!canvas) return;

    this.killCanvasOpacityTween();
    gsap.set(canvas, { opacity: CRT_OFF_OPACITY });

    const tl = gsap.timeline({
      onComplete: () => {
        document.body.classList.remove('crt-intro-active');
        document.body.style.background = '#000000';
      }
    });
    tl.to(canvas, { opacity: CRT_HIGH_OPACITY, duration: 1.5, ease: 'power2.inOut' });
    tl.to(canvas, { opacity: CRT_LOW_OPACITY, duration: 1, ease: 'power2.inOut' });
  }

  setPrintBackdrop(isPrint) {
    if (!this.enabled) return;
    document.body.classList.toggle('crt-print-page', isPrint);
    if (isPrint) {
      document.body.style.background = '#000000';
    }
  }

  tweenSectionOpacity(sectionKey) {
    const canvas = this.getCanvasEl();
    if (!canvas) return;

    this.killCanvasOpacityTween();

    if (sectionKey === 'print') {
      gsap.to(canvas, {
        opacity: CRT_OFF_OPACITY,
        duration: SECTION_CANVAS_FADE_DURATION,
        ease: 'power2.inOut',
        overwrite: true
      });
      return;
    }

    const tl = gsap.timeline({ overwrite: true });
    tl.to(canvas, { opacity: CRT_HIGH_OPACITY, duration: SECTION_CANVAS_FADE_DURATION, ease: 'power2.inOut' });
    tl.to(canvas, { opacity: CRT_LOW_OPACITY, duration: SECTION_CANVAS_FADE_DURATION, ease: 'power2.inOut' });
  }

  hideCanvasForReel() {
    if (!this.enabled || this.currentSection !== 'reel') return;

    const canvas = this.getCanvasEl();
    if (!canvas) return;

    this.killCanvasOpacityTween();
    gsap.to(canvas, {
      opacity: CRT_REEL_PLAY_OPACITY,
      duration: SECTION_CANVAS_FADE_DURATION,
      ease: 'power2.inOut',
      overwrite: true
    });
    this.reelCanvasHidden = true;
  }

  restoreCanvasOpacity() {
    if (!this.enabled || !this.reelCanvasHidden) return;

    this.reelCanvasHidden = false;
    this.tweenSectionOpacity(this.currentSection);
  }

  changeSection(sectionKey) {
    if (!this.enabled || !this.crt) return;

    this.reelCanvasHidden = false;
    this.currentSection = sectionKey;
    this.setPrintBackdrop(sectionKey === 'print');
    this.tweenSectionOpacity(sectionKey);
    this.crt.applySection(sectionKey);
  }

  changeColors() {}
}
